import { useState } from 'react'
import {
  api,
  type ComplianceAnswer,
  type EvaluationDetail,
  type OverrideCause,
  type VerdictCompliance,
  type VerdictRow,
  type VerdictState,
} from '../api'
import { ANSWER_LABEL, CAUSE_LABEL, OUTCOME_BADGE, OUTCOME_LABEL, answerOf } from '../agreementWords'
import { benchmarkSays } from '../benchmarkWords'
import { layerLabel, says } from '../limits'
import { amount, asAt, covers, room, rowName, why } from '../verdictWords'

/**
 * One verdict, with everything GUARD judged, for compliance to take the final call on —
 * proposal §6 second-release gate; IMPLEMENTATION.md decision 53.
 *
 * **GUARD's side first and in full**: the verdict, what the limit says and covers, what was
 * found, the room left, why, each issuer or country with its figure, where the limit comes
 * from and which holdings file was judged. Compliance's side beside it: its own verdict,
 * preset to GUARD's, and where it differs from a verdict GUARD decided, the cause and a note.
 * The reviews before stay listed, since a revised review keeps the one it replaced.
 */

const STATE_LABEL: Record<VerdictState, string> = {
  breach: 'Breach',
  not_evaluable: 'Not evaluable',
  warn: 'Warn',
  pass: 'Pass',
}

const ANSWERS: ComplianceAnswer[] = ['within', 'breach', 'not_evaluable']
const CAUSES: OverrideCause[] = ['limit', 'data', 'reading', 'accepted']
const SHOWN_GROUPS = 20

function said(e: unknown): string {
  return e instanceof Error ? e.message : String(e)
}

export function ComplianceReviewPanel({
  detail,
  verdict,
  compliance,
  canReview,
  position,
  onPrevious,
  onNext,
  onBack,
  onSaved,
}: {
  detail: EvaluationDetail
  verdict: VerdictRow
  compliance: VerdictCompliance | undefined
  canReview: boolean
  /** Where this verdict is among the fund's, such as `3 of 14`. */
  position: string
  onPrevious?: () => void
  onNext?: () => void
  onBack: () => void
  onSaved: (ruleId: string, saved: VerdictCompliance, next: boolean) => void
}) {
  const v = verdict
  const day = asAt(detail.snapshot_as_of)
  const words = v.template === 'narrative'
  const guard = answerOf(v.state)
  const current = compliance?.review ?? null
  const [answer, setAnswer] = useState<ComplianceAnswer>(current?.answer ?? guard)
  const [cause, setCause] = useState<OverrideCause | ''>(current?.cause ?? '')
  const [note, setNote] = useState(current?.note ?? '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)

  const overriding = answer !== guard && v.state !== 'not_evaluable'
  const ready = !overriding || (cause !== '' && note.trim() !== '')
  const reason = why(v, detail)
  const headroom = room(v)

  async function save(next: boolean) {
    setBusy(true)
    setError(null)
    setDone(null)
    try {
      const saved = await api.reviewVerdict(detail.id, v.rule_id, {
        answer,
        cause: answer === guard ? null : cause || null,
        note,
      })
      setDone('Review saved.')
      onSaved(v.rule_id, saved, next)
    } catch (e) {
      setError(said(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="compliance-review" data-testid="compliance-review">
      <div className="evaluations-head">
        <button type="button" className="ghost" onClick={onBack}>
          ← All verdicts
        </button>
        <nav className="fund-stepper" aria-label="Step through the verdicts">
          <button type="button" className="ghost" disabled={!onPrevious} onClick={onPrevious}>
            ← Previous
          </button>
          <span data-testid="review-position">{position}</span>
          <button type="button" className="ghost" disabled={!onNext} onClick={onNext}>
            Next →
          </button>
        </nav>
      </div>
      <h4>{rowName(v.rule_key)}</h4>
      <p className="note">
        {detail.portfolio_code}’s holdings as at {day}
      </p>

      <div className="review-columns">
        <div>
          <h5>GUARD’s verdict</h5>
          <div className="table-scroll">
            <table className="grid facts-table" data-testid="review-facts">
              <tbody>
                <tr>
                  <th scope="row">Verdict</th>
                  <td>
                    <span className={`verdict-count ${v.state}`}>{STATE_LABEL[v.state]}</span> as at {day}
                  </td>
                </tr>
                <tr>
                  <th scope="row">Limit</th>
                  <td>
                    {words
                      ? (v.narrative_text ?? '')
                      : says({ operator: v.operator, threshold: v.threshold, measure: v.measure, template: v.template })
                          .text}
                  </td>
                </tr>
                {v.benchmark && (
                  <tr>
                    <th scope="row">Benchmark</th>
                    <td>{benchmarkSays(v)}</td>
                  </tr>
                )}
                {!words && (
                  <tr>
                    <th scope="row">Covers</th>
                    <td>{covers(v)}</td>
                  </tr>
                )}
                {!words && (
                  <tr>
                    <th scope="row">Found</th>
                    <td>{amount(v.value, v.unit) || '—'}</td>
                  </tr>
                )}
                {headroom && (
                  <tr>
                    <th scope="row">Headroom</th>
                    <td>{headroom}</td>
                  </tr>
                )}
                {reason && (
                  <tr>
                    <th scope="row">Why</th>
                    <td>{reason}</td>
                  </tr>
                )}
                <tr>
                  <th scope="row">Holdings considered</th>
                  <td>{v.holdings_considered}</td>
                </tr>
                <tr>
                  <th scope="row">Layer</th>
                  <td>{layerLabel(v.layer)}</td>
                </tr>
                <tr>
                  <th scope="row">Applies through</th>
                  <td>{v.origin === 'portfolio' ? 'This fund' : v.origin.replace(':', ' ')}</td>
                </tr>
                {v.source_anchor && (
                  <tr>
                    <th scope="row">Cell</th>
                    <td>{v.source_anchor}</td>
                  </tr>
                )}
                <tr>
                  <th scope="row">Holdings file</th>
                  <td>{detail.snapshot_filename}</td>
                </tr>
                <tr>
                  <th scope="row">Evaluated</th>
                  <td>
                    {new Date(detail.ran_at).toLocaleString()}
                    {detail.run_by ? ` by ${detail.run_by}` : ''}
                  </td>
                </tr>
                {(detail.facts ?? []).map((f) => (
                  <tr key={f.name}>
                    <th scope="row">{f.label}</th>
                    <td>{f.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {v.groups.length > 0 && (
            <div className="table-scroll">
              <table className="grid" data-testid="review-groups">
                <thead>
                  <tr>
                    <th>Each</th>
                    <th>Found</th>
                    <th>Headroom</th>
                    <th>Holdings</th>
                    <th>Verdict</th>
                  </tr>
                </thead>
                <tbody>
                  {v.groups.slice(0, SHOWN_GROUPS).map((g) => (
                    <tr key={g.key ?? 'all'}>
                      <td>{g.key ?? 'All'}</td>
                      <td>
                        {amount(g.value, v.unit)}
                        {g.benchmark !== null && g.benchmark !== undefined && ` · benchmark ${amount(g.benchmark, v.unit)}`}
                      </td>
                      <td>{room({ ...v, headroom: g.headroom })}</td>
                      <td className="limit">{g.holdings}</td>
                      <td>
                        <span className={`verdict-count ${g.state}`}>{STATE_LABEL[g.state]}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {v.groups.length > SHOWN_GROUPS && (
                <p className="clause">and {v.groups.length - SHOWN_GROUPS} more</p>
              )}
            </div>
          )}

          {words && v.review && v.review.terms.length > 0 && (
            <ul className="verdict-groups" data-testid="review-words">
              {v.review.terms.map((t) => (
                <li key={t.term}>
                  {t.term}: {t.holdings} holding{t.holdings === 1 ? '' : 's'}
                  {t.pct_of_nav === null ? '' : `, ${t.pct_of_nav.toFixed(2)}% of NAV`}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div>
          <h5>Compliance review</h5>
          {compliance && (
            <p>
              <span className={`verdict-count ${OUTCOME_BADGE[compliance.outcome]}`} data-testid="review-outcome">
                {OUTCOME_LABEL[compliance.outcome]}
              </span>
            </p>
          )}
          {compliance?.outcome === 'changed_since_review' && current && (
            <p className="check-differs">
              GUARD’s verdict was {STATE_LABEL[current.guard_state]} when compliance reviewed it, and is{' '}
              {STATE_LABEL[v.state]} now.
            </p>
          )}
          {error && (
            <p className="error" role="alert">
              {error}
            </p>
          )}
          {done && (
            <p className="done" role="status">
              {done}
            </p>
          )}

          {canReview && detail.reviewable ? (
            <form
              className="review-form"
              data-testid="review-form"
              onSubmit={(e) => {
                e.preventDefault()
                void save(false)
              }}
            >
              <fieldset className="review-answer">
                <legend>Compliance’s verdict</legend>
                {ANSWERS.map((a) => (
                  <label key={a}>
                    <input
                      type="radio"
                      name="compliance-answer"
                      value={a}
                      checked={answer === a}
                      onChange={() => setAnswer(a)}
                    />
                    {ANSWER_LABEL[a]}
                    {a === guard && <span className="clause">as GUARD</span>}
                  </label>
                ))}
              </fieldset>
              {overriding && (
                <label>
                  Why it differs
                  <select value={cause} onChange={(e) => setCause(e.target.value as OverrideCause | '')}>
                    <option value="">Choose why</option>
                    {CAUSES.map((c) => (
                      <option key={c} value={c}>
                        {CAUSE_LABEL[c]}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <label>
                Note
                <textarea
                  rows={3}
                  value={note}
                  placeholder={
                    overriding
                      ? 'What compliance found, such as the issuer’s group under the mandate'
                      : 'Anything worth keeping with this review'
                  }
                  onChange={(e) => setNote(e.target.value)}
                />
              </label>
              <p className="actions">
                <button type="submit" disabled={busy || !ready}>
                  Save
                </button>
                {onNext && (
                  <button type="button" disabled={busy || !ready} onClick={() => void save(true)}>
                    Save and next
                  </button>
                )}
              </p>
            </form>
          ) : current ? (
            <table className="grid facts-table" data-testid="review-current">
              <tbody>
                <tr>
                  <th scope="row">Compliance’s verdict</th>
                  <td>{ANSWER_LABEL[current.answer]}</td>
                </tr>
                {current.cause && (
                  <tr>
                    <th scope="row">Why it differs</th>
                    <td>{CAUSE_LABEL[current.cause]}</td>
                  </tr>
                )}
                {current.note && (
                  <tr>
                    <th scope="row">Note</th>
                    <td>{current.note}</td>
                  </tr>
                )}
              </tbody>
            </table>
          ) : (
            <p className="empty">Compliance has not reviewed this verdict yet.</p>
          )}
          {canReview && !detail.reviewable && (
            <p className="note">A later evaluation of this day stands, and its verdicts are the ones to review.</p>
          )}

          {(compliance?.history.length ?? 0) > 0 && (
            <>
              <h5>Reviews</h5>
              <ul className="review-history" data-testid="review-history">
                {compliance!.history.map((h) => (
                  <li key={`${h.reviewed_at}-${h.answer}-${h.note ?? ''}`}>
                    {ANSWER_LABEL[h.answer]} when GUARD said {STATE_LABEL[h.guard_state]}
                    {h.cause && ` · ${CAUSE_LABEL[h.cause]}`}
                    <span className="clause">
                      {h.reviewed_by ?? ''} · {new Date(h.reviewed_at).toLocaleString()}
                    </span>
                    {h.note && <span className="clause">{h.note}</span>}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </div>
    </section>
  )
}
